<template>
  <div class="adn-text-area">
    <label v-if="label" :for="textAreaId" data-test-label>{{ label }}</label>
    <textarea
      v-if="edition"
      v-model="value"
      :maxlength="maxContentLength"
      :placeholder="placeholder"
      class="adn-text-area__text-area"
      :class="{
        'adn-text-area__text-area--readonly': readonly,
        'adn-text-area__text-area--disabled': disabled,
      }"
      :disabled="disabled"
      :readonly="readonly"
      @change="onTextChange"
      @keyup="onTextChange"
      @paste="onTextChange"
    />
    <div v-else class="ql-editor display-only" v-html="value" />
    <span data-test-content-length class="adn-text-area__content-length">
      <slot name="content-length" :content-length="contentLength" :visible-content-length="visibleContentLength" />
    </span>
  </div>
</template>

<script lang="ts">
import uuidv4 from '@b2ag/utils/src/uuid'
import { defineComponent, onMounted, ref, toRefs } from 'vue'

export default defineComponent({
  name: 'AdnTextArea',
  props: {
    label: {
      type: String,
      default: null,
    },
    initialValue: {
      type: String,
      default: '',
    },
    modelValue: {
      type: String,
      default: '',
    },
    placeholder: {
      type: String,
      default: null,
    },
    maxContentLength: {
      type: Number,
      default: null,
    },
    edition: {
      type: Boolean,
      default: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    truncateVisible: {
      type: Boolean,
      default: true,
    },
    liteModeToolbar: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['input', 'update:modelValue'],
  setup(props, { emit }) {
    const { initialValue, maxContentLength, truncateVisible, modelValue } = toRefs(props)
    const editor = ref(null)
    const contentLength = ref(0)
    const visibleContentLength = ref(0)
    const textAreaId = ref<string | undefined>(undefined)
    const value = ref<string | undefined>(undefined)
    const previousValue = ref<string | undefined>(undefined)

    const initializeData = async () => {
      value.value = initialValue.value || modelValue.value
      previousValue.value = initialValue.value || modelValue.value
      if (maxContentLength.value > 0) {
        value.value = value.value.substring(0, maxContentLength.value)
      }
      countCharacters()
    }

    const onTextChange = () => {
      countCharacters()
      if (maxCharactersReached()) {
        value.value = value.value?.substring(0, maxContentLength.value) || ''
      } else {
        emit('input', value.value)
        emit('update:modelValue', value.value)
      }
    }

    const countCharacters = () => {
      contentLength.value = value.value?.length || 0
      visibleContentLength.value = value.value?.length || 0
    }

    const maxCharactersReached = () => {
      return (
        maxContentLength.value &&
        (truncateVisible.value
          ? visibleContentLength.value > maxContentLength.value
          : contentLength.value > maxContentLength.value)
      )
    }

    onMounted(() => {
      textAreaId.value = uuidv4()
      initializeData()
    })

    return {
      editor,
      contentLength,
      visibleContentLength,
      textAreaId,
      value,
      previousValue,
      initializeData,
      onTextChange,
      countCharacters,
      maxCharactersReached,
    }
  },
})
</script>
<style scoped lang="scss">
.adn-text-area {
  width: 100%;
  height: 100%;

  &__content-length {
    color: var(--color-inactive-dark);
    font-size: var(--font-size-s);
  }

  .ql-editor.display-only {
    padding: 0;
    min-height: 0;
    overflow-y: unset;
    line-height: 1.5;
  }

  :deep(.ql-editor) {
    font-size: var(--font-size-m);
    min-height: 15rem;
    overflow-y: auto;
  }

  :deep(.ql-toolbar.ql-snow) {
    background: var(--color-border);
    border: 1px solid var(--color-text-light);
    border-radius: var(--border-radius-1) var(--border-radius-1) 0 0;
  }

  .ql-container.ql-snow {
    background: var(--color-background-light);
    border: 1px solid var(--color-text-light);
    border-radius: 0 0 var(--border-radius-1) var(--border-radius-1);
    border-top: none;
  }

  &__text-area {
    resize: none;
    width: 100%;
    height: 100%;
    color: var(--color-text-light);
    outline: none;
    border-radius: var(--border-radius-1);
    padding: 1rem;

    &:hover {
      box-shadow: var(--shadow);
    }

    &:focus-within {
      box-shadow: var(--shadow);
      color: var(--color-black);
    }

    &--disabled {
      box-shadow: none;
      border-color: var(--color-inactive-light);
      color: var(--color-inactive-light);
      cursor: not-allowed;

      &:hover {
        box-shadow: none;
      }
    }

    &--readonly {
      &:hover {
        box-shadow: none;
      }
    }
  }

  &__editor {
    &:hover {
      box-shadow: var(--shadow);
    }

    &:focus-within {
      box-shadow: var(--shadow);
    }

    &--readonly {
      &:hover {
        box-shadow: none;
      }
    }

    &--disabled {
      cursor: not-allowed;

      :deep() {
        .ql-editor > *,
        .ql-toolbar button,
        .ql-picker-label {
          cursor: not-allowed;
        }

        .ql-editor {
          background-color: var(--color-inactive-light);
        }
      }

      &:hover {
        box-shadow: none;
      }
    }
  }
}
</style>
